import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    Guideline_Role_Enum,
    Guideline_Status_Enum,
    Guideline_Type_Enum,
    GuidelineFragment,
    Guidelines_Insert_Input,
    UpdateGuidelineMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    AgencyOperationsAdminVenueBookingGuidelineCreateFragment, 
    AgencyOperationsAdminVenueBookingGuidelineCreateSchema, 
    AgencyOperationsAdminVenueBookingGuidelineUpdateFragment, 
    AgencyOperationsAdminVenueBookingGuidelineUpdateSchema 
} from "src/components/agency/operations/administrator/venues/bookings/guidelines/AgencyOperationsAdminVenueBookingGuidelineSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = AgencyOperationsAdminVenueBookingGuidelineCreateFragment

let initialState: FormPayloadType = {
    name: "",
    description: "",
    tenant_required: false,
    venue_required: false,
    talent_required: false,
    document_id: null,
    metadata: null,
};

export const CreateAgencyOperationsAdminVenueBookingGuidelineDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().guidelines;
    const { loading, errors } = useAppState().guidelines;
    const { currentItemId: currentBookingId } = useAppState().bookings;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Guidelines_Insert_Input) => {
        return createItem({ booking_id: currentBookingId, type: Guideline_Type_Enum.Booking, parent_guideline_type: Guideline_Type_Enum.Booking, ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, GuidelineFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminVenueBookingGuidelineCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateAgencyOperationsAdminVenueBookingGuidelineAction = () => {
    return (
        <PageDrawerAction<GuidelineFragment>
            title='Add Guideline'
            action={CreateAgencyOperationsAdminVenueBookingGuidelineDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: AgencyOperationsAdminVenueBookingGuidelineUpdateFragment = {
    status: Guideline_Status_Enum.Active,
    role: Guideline_Role_Enum.Info,
    name: "",
    description: "",
    tenant_required: false,
    venue_required: false,
    talent_required: false,
    document_id: null,
    metadata: null,
    tenant_confirmed: false,
    venue_confirmed: false,
    talent_confirmed: false,
    tenant_completed: false,
    venue_completed: false,
    talent_completed: false,    
    target_booking_type: null,
    target_booking_status: null,
};

export const UpdateAgencyOperationsAdminVenueBookingGuidelineDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().guidelines;
    const { currentItem, loading, errors } = useAppState().guidelines;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: AgencyOperationsAdminVenueBookingGuidelineUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<AgencyOperationsAdminVenueBookingGuidelineUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateGuidelineMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<AgencyOperationsAdminVenueBookingGuidelineUpdateFragment, GuidelineFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminVenueBookingGuidelineUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateAgencyOperationsAdminVenueBookingGuidelineAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().guidelines;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.guidelines.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<GuidelineFragment>
            title='Edit Guideline'
            action={UpdateAgencyOperationsAdminVenueBookingGuidelineDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditAgencyOperationsAdminVenueBookingGuidelineAction = () => {
    return (
        <PageDrawerAction<GuidelineFragment>
            title='Edit Guideline'
            action={UpdateAgencyOperationsAdminVenueBookingGuidelineDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}